import React, { useEffect, useRef, useState } from "react";
import EditableText from "../components/EditableText";
import type BaseObject from "../models/BaseObject";
import type Page from "../models/Page";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import ActorsView from "./ActorsView";
import type Room from "../models/Room";
import type Floor from "../models/Floor";
import { PagePrintInfo } from "../models/PagePrintInfo";

import Logger from "../Logger";
import { isUiModeExpert, isUiModeUser } from "../uiMode";
import type Actor from "../models/Actor";
import type Cabinet from "../models/Cabinet";
import type Consumer from "../models/Consumer";
import { useRedraw } from "../models/useRerender";

const logger = new Logger();

export default function CabinetView(props: {
  mainPage: Page;
  fixed: boolean;
  print?: PagePrintInfo;
  adjustActive?: boolean;
}) {
  const { mainPage, print, fixed, adjustActive } = props;

  const timer = useRef<any>(0);
  const [visibility, setVisibility] = useState(false);

  const scrollContentEquivalent = useRef<HTMLDivElement>(null);
  const scrollContent = useRef<HTMLDivElement>(null);
  const [cabinetsElements, setCabinetsElements] = useState(new Array<any>());
  const [showUsedActorsOnly, setShowUsedActorsOnly] = useState(state.cabinets.asArray()[0]?.showUsedActorsOnly);

  const redraw = useRedraw();

  let roomOrFloor: Room | Floor | undefined = mainPage.parent as Room | Floor;

  function calcVisibility() {
    let vis = true;

    if (!isUiModeExpert()) {
      if (
        state.selectionManager.selectedObject?.className == "Consumer" &&
        (state.selectionManager.selectedObject?.parent == roomOrFloor ||
          state.selectionManager.selectedObject?.parent?.parent == roomOrFloor)
      ) {
        //
        // selected Object is a Consumer on this page
        //
        vis = true;
      } else if (state.selectionManager.selectedObject?.className == "Actor") {
        //
        // selected Object is an Actor
        //
        const actor = state.selectionManager.selectedObject as Actor;
        actor.hasConnectionOnPage(mainPage, true);
        vis = true;
      } else if (state.selectionManager.selectedObject?.className == "Channel") {
        //
        // selected Object is an Channel
        //
        const actor = state.selectionManager.selectedObject.parent as Actor;
        actor.hasConnectionOnPage(mainPage, true);
        vis = true;
      } else {
        vis = false;
      }
    } else {
      vis = true;
    }

    return vis;
  }

  function calcAndSetVisibility() {
    let vis = calcVisibility();

    if (timer.current) {
      clearTimeout(timer.current);
    }

    if (!vis) {
      timer.current = setTimeout(() => {
        setVisibility(vis);
        timer.current = 0;
      }, 200);
    } else {
      setVisibility(vis);
      timer.current = 0;
    }
    state.cabinets.forEach((cabinet) => {
      cabinet?.actors.forEach((actor) => {
        cabinet?.actorsColapseMode.set(actor.id, actor.collapseMode);
      });
    });
  }

  function OneCabinetHeaderView(props: { oneCabinet }) {
    const { oneCabinet } = props;

    if (oneCabinet.tagOrName != "") {
      return (
        <div
          className="cabinetHeader"
          id="cabinetHeader"
          style={{
            width: "calc(100% - 3px)",
            pointerEvents: "auto",
            borderRadius: "5px",
            marginBottom: "5px",
            border: oneCabinet.selected && !print ? "solid 3px var(--selectedOrange)" : undefined, // no itx
          }}
        >
          {oneCabinet && (
            <EditableText
              obj={oneCabinet}
              property="tagOrName" // no itx
              propertyType="text"
              style={{ padding: "0px", textAlign: "center" }}
              maxCharacters={state.maxCharLimit.text}
              disableEdit={true}
            />
          )}
        </div>
      );
    } else {
      return null;
    }
  }
  useOnChangeWatcher(state.selectionManager, (changedProperty: string | any | undefined, changedObject: BaseObject) => {
    calcAndSetVisibility();
    initCabinetsElements();
    if (isUiModeUser()) {
      mainPage.updateConnections();
    }

    return false;
  });

  useEffect(() => {
    if (scrollContentEquivalent.current && scrollContent.current) {
      scrollContentEquivalent.current.style.height = `${scrollContent.current.clientHeight}px`;
    }
  });

  useEffect(() => {
    calcAndSetVisibility();
    initCabinetsElements();
  }, []);

  useEffect(() => {
    calcAndSetVisibility();
    initCabinetsElements();
    mainPage.recalcConnections.value++;
  }, [showUsedActorsOnly]);

  useOnChangeWatcher(roomOrFloor, (changedProperty: string | any | undefined, changedObject: BaseObject) => {
    mainPage.updateConnections();
    calcAndSetVisibility();
    initCabinetsElements();
    return true;
  });

  useOnChangeWatcher(state.uiMode, (changedProperty: string | any | undefined, changedObject: BaseObject) => {
    calcAndSetVisibility();
    initCabinetsElements();
    return true;
  });

  function initCabinetsElements() {
    const calculatedCabinetsElements = new Array<any>();
    let toShowCabinets = new Array<Cabinet>();

    if (isUiModeUser()) {
      const consumer = state.selectionManager.selectedObject as Consumer;
      if (consumer?.isConsumer) {
        // Show only the selected consumer's cabinet
        const cabinet = consumer.getConnectedChannel()?.parent?.parent as Cabinet;
        if (cabinet) {
          toShowCabinets.push(cabinet);
        } else {
          return null;
        }
      } else {
        return null;
      }
    } else {
      if (showUsedActorsOnly) {
        toShowCabinets = state.cabinets.findAsArray((cabinet) => cabinet.hasConnectionOnPage(mainPage, false));
      } else {
        toShowCabinets = state.cabinets.asArray();
      }
    }

    toShowCabinets.forEach((cabinet) => {
      calculatedCabinetsElements.push(<OneCabinetHeaderView oneCabinet={cabinet} />);
      calculatedCabinetsElements.push(<ActorsView fixed={fixed} page={mainPage} cabinet={cabinet} print={print} />);
    });

    if (state.cabinets.size > 0) {
      calculatedCabinetsElements.push(<ShowButton />);
    }

    setCabinetsElements(calculatedCabinetsElements);
    redraw();
  }

  const thisElement = useRef<HTMLDivElement>(null);

  setTimeout(() => {
    if (thisElement.current) {
      thisElement.current.style.opacity = "1";
    }
  }, 100);

  function ShowButton(props: {}) {
    if (state.cabinets.size == 0) {
      return <div></div>;
    }
    return (
      <button
        className="button"
        style={{
          width: "75%",
          pointerEvents: "auto",
          margin: "0px",
          alignSelf: "center",
          minWidth: "52px",
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={
          showUsedActorsOnly
            ? state.translate("CabinetView.showAll", "alle anzeigen")
            : state.translate("CabinetView.hideUnused", "unbenutzte ausblenden")
        }
        data-tooltip-place="right"
        onClick={(ev) => {
          ev.stopPropagation();
          state.cabinets.forEach((cabinet) => {
            if (cabinet) {
              cabinet.showUsedActorsOnly = !cabinet.showUsedActorsOnly;
              cabinet.raiseOnChange("showUsedActorsOnly");
              setShowUsedActorsOnly(cabinet.showUsedActorsOnly);
            }
          });
        }}
      >
        <i className={`la ${showUsedActorsOnly ? "la-eye" : "la-eye-slash"}`}></i>
      </button>
    );
  }

  logger.log("render all cabinet");

  const style: any = {
    overflow: "hidden",
    width: "300px",
    backgroundColor: "transparent",
    display: "flex",
    flexDirection: "column",
    visibility: visibility ? "inherit" : "hidden",
    pointerEvent: "none",
    opacity: "0",
    transition: "opacity 1s ease-in-out",
  };

  return (
    <div
      id="cabinet"
      ref={thisElement}
      className={`scrollcontainer cabinet ${fixed ? "fixed" : ""}`}
      style={{ ...style, pointerEvents: "none" }}
      onScroll={() => {
        mainPage.updateConnections();
      }}
      onMouseDown={(ev) => {
        ev.stopPropagation();
      }}
      onMouseMove={(ev) => {
        ev.stopPropagation();
      }}
      onMouseUp={(ev) => {
        ev.stopPropagation();
      }}
      onClick={(ev) => {
        if (print == undefined) {
          if (!adjustActive) {
            ev.stopPropagation();
            // state.selectionManager.selectedObject = cabinet;
          }
        }
      }}
    >
      <div
        className="scrollbar"
        style={{
          position: "absolute",
          width: "50px",
          left: "0px",
          height: "100%",
          pointerEvents: "auto",
          backgroundColor: "lightgrey",
          overflowY: "auto",
        }}
        onScroll={(ev) => {
          if (scrollContent.current) {
            scrollContent.current.style.marginTop = `${-ev.currentTarget.scrollTop}px`;
            mainPage.recalcConnections.value++;
          }
        }}
      >
        <div ref={scrollContentEquivalent} style={{ height: "100px" }}></div>
      </div>

      <div
        style={{
          position: "relative",
          overflow: "visible",
          width: "500px",
          marginTop: "0px",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          flexGrow: "1",
          flexShrink: "0",
        }}
      >
        <div
          ref={scrollContent}
          className="actorArea"
          style={{
            position: "relative",
            marginLeft: fixed ? "10px" : "0px", // this margin keeps the scrollbar visible
            direction: "ltr",
            pointerEvents: "none",
            display: "flex",
            flexDirection: "column",
            flexGrow: "1",
            flexShrink: "0",
            minHeight: "100%",
          }}
          onWheel={(ev) => {
            if (scrollContent.current && scrollContentEquivalent.current) {
              if (scrollContentEquivalent.current.parentElement) {
                scrollContentEquivalent.current.parentElement.scrollTop =
                  scrollContentEquivalent.current.parentElement.scrollTop + ev.deltaY;
              }
            }
          }}
        >
          <div
            style={{
              position: "relative",
              borderRadius: "5px",
              width: "max-content",
              maxWidth: "100px",
              backgroundColor: "lightgrey",
              paddingLeft: "5px",
              paddingTop: "3px",
              paddingRight: "5px",
              paddingBottom: "10px",
              minHeight: "100%",
              display: "flex",
              flexShrink: "0",
              flexDirection: "column",
            }}
          >
            {cabinetsElements}
          </div>
        </div>
      </div>
    </div>
  );
  // } else {
  //   logger.error(`room (and floor) has not cabinet selected  ${floor?.tag} ${roomOrFloor?.tag}`);
  //   return (
  //     <div
  //       ref={thisElement}
  //       className="cabinet"
  //       style={{
  //         position: "absolute",
  //         backgroundColor: "var(--error-color)",
  //         color: "var(--knxColorWhite)",
  //         zIndex: "1",
  //         cursor: "pointer",
  //         borderRadius: "5px",
  //         width: "200px",
  //         textAlign: "center",
  //         visibility: isUiModeExpert() ? "visible" : "hidden",
  //         height: fixed ? "100%" : undefined,
  //       }}
  //       onClick={() => {
  //         floor?.treeViewModel.handleProperties();
  //       }}
  //     >
  //       {print
  //         ? state.translate("CabinetView.noCabinetDeposited", "Kein Schaltschrank hinterlegt")
  //         : state.translate(
  //             "CabinetView.noCabinetChooseOrcreateOne",
  //             "  Kein Schaltschrank Bitte wähle für den Raum oder die Etage einen Schaltschrank aus",
  //           )}
  //     </div>
  //   );
  // }
}
